export type VisitorRegionSource = 'amplify' | 'fallback' | 'development';

export type VisitorRegion = {
  /**
   * HK = Hong Kong, US = United States, CN = Mainland China, OTHER = all other regions.
   */
  countryCode: string;
  regionCode: string | null;
  regionName: string | null;
  city: string | null;
  timezoneId: string | null;
  isXinjiang: boolean;
  isHK: boolean;
  isUS: boolean;
  /** Hong Kong and United States visitors use the restricted site experience. */
  isRestrictedRegion: boolean;
  source: VisitorRegionSource;
};

const GEO_ENDPOINT = '/__geo/visitor-country.json';
const DEVELOPMENT_REGION_QUERY_KEY = 'jlbhkRegion';
const DEVELOPMENT_PROVINCE_QUERY_KEY = 'jlbhkProvince';
const CHINA_REGIONAL_GEO_ENDPOINT =
  import.meta.env.VITE_CHINA_REGIONAL_GEO_ENDPOINT ||
  'https://ipwho.is/?fields=success,country_code,region,region_code,city,timezone.id';
const CHINA_REGIONAL_GEO_CACHE_KEY = 'jlbhk_china_regional_geo_v1';
const CHINA_REGIONAL_GEO_TIMEOUT_MS = 2500;

/**
 * Fail open when the region endpoint is unavailable or not configured.
 * Unknown visitors are treated as unrestricted until a confirmed HK/US result arrives.
 */
export const UNKNOWN_FALLBACK_REGION: VisitorRegion = {
  countryCode: 'OTHER',
  regionCode: null,
  regionName: null,
  city: null,
  timezoneId: null,
  isXinjiang: false,
  isHK: false,
  isUS: false,
  isRestrictedRegion: false,
  source: 'fallback',
};

let pendingRegionPromise: Promise<VisitorRegion> | null = null;
let cachedRegion: VisitorRegion | null = null;
let lastLoggedSignature: string | null = null;

function normalizeCountryCode(value: unknown) {
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toUpperCase();
  if (normalized === 'OTHER') return normalized;
  if (/^[A-Z]{2}$/.test(normalized)) return normalized;

  return null;
}

function normalizeOptionalText(value: unknown) {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized || null;
}

function normalizeRegionCode(value: unknown) {
  const normalized = normalizeOptionalText(value)?.toUpperCase() || null;
  return normalized && /^[A-Z0-9-]{1,10}$/.test(normalized) ? normalized : null;
}

export function isXinjiangRegion(
  regionCode?: string | null,
  regionName?: string | null,
  city?: string | null,
  timezoneId?: string | null,
) {
  const code = String(regionCode || '').trim().toUpperCase();
  if (['XJ', '65', 'CN-65'].includes(code)) return true;

  const locationText = `${regionName || ''} ${city || ''} ${timezoneId || ''}`.toLowerCase();
  return /xinjiang|新疆|شىنجاڭ|urumqi|ürümqi|乌鲁木齐|ئۈرۈمچى|asia\/urumqi/.test(locationText);
}

function readCachedChinaRegionalGeo() {
  try {
    const value = window.sessionStorage.getItem(CHINA_REGIONAL_GEO_CACHE_KEY);
    if (!value) return null;
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return null;

    const record = parsed as Record<string, unknown>;
    const regionCode = normalizeRegionCode(record.regionCode);
    const regionName = normalizeOptionalText(record.regionName);
    const city = normalizeOptionalText(record.city);
    const timezoneId = normalizeOptionalText(record.timezoneId);
    return {
      regionCode,
      regionName,
      city,
      timezoneId,
      isXinjiang: isXinjiangRegion(regionCode, regionName, city, timezoneId),
    };
  } catch {
    return null;
  }
}

function cacheChinaRegionalGeo(value: {
  regionCode: string | null;
  regionName: string | null;
  city: string | null;
  timezoneId: string | null;
}) {
  try {
    window.sessionStorage.setItem(CHINA_REGIONAL_GEO_CACHE_KEY, JSON.stringify(value));
  } catch {
    // Ignore unavailable storage, for example private browsing mode.
  }
}

async function fetchChinaRegionalGeo() {
  if (typeof window === 'undefined') return null;

  const cached = readCachedChinaRegionalGeo();
  if (cached) return cached;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), CHINA_REGIONAL_GEO_TIMEOUT_MS);

  try {
    const response = await fetch(CHINA_REGIONAL_GEO_ENDPOINT, {
      cache: 'no-store',
      mode: 'cors',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;

    const payload: unknown = await response.json();
    if (!payload || typeof payload !== 'object') return null;
    const record = payload as Record<string, unknown>;
    if (record.success !== true || normalizeCountryCode(record.country_code) !== 'CN') return null;

    const timezone = record.timezone;
    const timezoneId =
      timezone && typeof timezone === 'object'
        ? normalizeOptionalText((timezone as Record<string, unknown>).id)
        : normalizeOptionalText(record['timezone.id']);
    const regionCode = normalizeRegionCode(record.region_code);
    const regionName = normalizeOptionalText(record.region);
    const city = normalizeOptionalText(record.city);
    const detail = {
      regionCode,
      regionName,
      city,
      timezoneId,
      isXinjiang: isXinjiangRegion(regionCode, regionName, city, timezoneId),
    };
    cacheChinaRegionalGeo(detail);
    return detail;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function normalizeIsHK(payload: Record<string, unknown>, countryCode: string) {
  if (typeof payload.isHK === 'boolean') return payload.isHK;
  if (typeof payload.isHongKong === 'boolean') return payload.isHongKong;

  return countryCode === 'HK';
}

function normalizeIsUS(payload: Record<string, unknown>, countryCode: string) {
  if (typeof payload.isUS === 'boolean') return payload.isUS;
  if (typeof payload.isUnitedStates === 'boolean') return payload.isUnitedStates;

  return countryCode === 'US';
}

function parseGeoPayload(payload: unknown): VisitorRegion | null {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const countryCode = normalizeCountryCode(record.countryCode);
  if (!countryCode) return null;

  // visitor-country.json itself is the unresolved fallback file. Amplify should
  // rewrite/redirect it to hk.json, us.json, cn.json or other.json, all of which set this flag.
  const resolvedByAmplify = record.resolvedByAmplify === true;

  const payloadSaysHongKong = normalizeIsHK(record, countryCode);
  const payloadSaysUnitedStates = normalizeIsUS(record, countryCode);
  const isHK = resolvedByAmplify && payloadSaysHongKong;
  const isUS = resolvedByAmplify && payloadSaysUnitedStates;
  const regionCode = normalizeRegionCode(record.regionCode);
  const regionName = normalizeOptionalText(record.regionName);
  const city = normalizeOptionalText(record.city);
  const timezoneId = normalizeOptionalText(record.timezoneId);
  const isXinjiang =
    countryCode === 'CN' &&
    (record.isXinjiang === true || isXinjiangRegion(regionCode, regionName, city, timezoneId));

  return {
    countryCode,
    regionCode,
    regionName,
    city,
    timezoneId,
    isXinjiang,
    // A static fallback file is not a confirmed IP result. Only an Amplify-resolved
    // HK/US payload can hide restricted content. Local development overrides are handled
    // separately by getDevelopmentRegion().
    isHK,
    isUS,
    isRestrictedRegion: isHK || isUS,
    source: resolvedByAmplify ? 'amplify' : 'fallback',
  };
}

function getDevelopmentRegion(): VisitorRegion | null {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null;

  const hostname = window.location.hostname.toLowerCase();
  if (!['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname)) return null;

  const override = normalizeCountryCode(
    new URLSearchParams(window.location.search).get(DEVELOPMENT_REGION_QUERY_KEY),
  );
  const countryCode = override || 'OTHER';
  const regionCode = normalizeRegionCode(
    new URLSearchParams(window.location.search).get(DEVELOPMENT_PROVINCE_QUERY_KEY),
  );

  const isHK = countryCode === 'HK';
  const isUS = countryCode === 'US';

  return {
    countryCode,
    regionCode,
    regionName: null,
    city: null,
    timezoneId: null,
    isXinjiang: countryCode === 'CN' && isXinjiangRegion(regionCode),
    isHK,
    isUS,
    isRestrictedRegion: isHK || isUS,
    source: 'development',
  };
}

function logVisitorRegion(region: VisitorRegion) {
  if (typeof console === 'undefined') return;

  const signature = `${region.countryCode}:${region.regionCode || ''}:${region.isRestrictedRegion}:${region.source}`;
  if (lastLoggedSignature === signature) return;
  lastLoggedSignature = signature;

  const payload = {
    countryCode: region.countryCode,
    regionCode: region.regionCode,
    regionName: region.regionName,
    city: region.city,
    isXinjiang: region.isXinjiang,
    isHongKong: region.isHK,
    isUnitedStates: region.isUS,
    isRestrictedRegion: region.isRestrictedRegion,
    restrictedContentVisible: !region.isRestrictedRegion,
    source: region.source,
  };

  if (region.source === 'fallback') {
    console.warn('[JL金利寶] visitor region unresolved; restricted content remains visible:', payload);
    return;
  }

  console.info('[JL金利寶] visitor region:', payload);
}

async function fetchAmplifyRegion() {
  const developmentRegion = getDevelopmentRegion();
  if (developmentRegion) {
    logVisitorRegion(developmentRegion);
    return developmentRegion;
  }

  const response = await fetch(GEO_ENDPOINT, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load visitor region: ${response.status}`);
  }

  const payload = await response.json();
  let region = parseGeoPayload(payload);

  if (!region) {
    throw new Error('Invalid visitor region payload');
  }

  if (region.countryCode === 'CN' && !region.isXinjiang) {
    const regionalDetail = await fetchChinaRegionalGeo();
    if (regionalDetail) {
      region = { ...region, ...regionalDetail };
    }
  }

  logVisitorRegion(region);
  return region;
}

export function getCachedAmplifyVisitorRegion() {
  return cachedRegion;
}

export async function getAmplifyVisitorRegion() {
  if (cachedRegion) return cachedRegion;

  if (!pendingRegionPromise) {
    pendingRegionPromise = fetchAmplifyRegion()
      .catch((error) => {
        if (typeof console !== 'undefined') {
          console.warn('[JL金利寶] failed to detect visitor region:', error);
        }

        logVisitorRegion(UNKNOWN_FALLBACK_REGION);
        return UNKNOWN_FALLBACK_REGION;
      })
      .then((region) => {
        cachedRegion = region;
        return region;
      })
      .finally(() => {
        pendingRegionPromise = null;
      });
  }

  return pendingRegionPromise;
}
